import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Divider,
  useToast,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Flex
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';

export default function AuthScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyId, setCompanyId] = useState('');
  const { currentUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // URLからcompany_idを取得する関数
  const getCompanyIdFromUrl = () => {
    const hostname = window.location.hostname;
    
    // localhost:5173の場合はlocalと表示
    if (hostname === 'localhost') {
      return 'localhost';
    }
    
    // [company_id].app.selectia.himawarin-lab.com の形式をチェック
    const regex = hostname.includes('dev-app') ? /^([^.]+)\.dev-app\.selectia\.himawarin-lab\.com$/ : /^([^.]+)\.app\.selectia\.himawarin-lab\.com$/;
    const match = hostname.match(regex);
    
    if (match) {
      return match[1]; // company_id部分を返す
    }
    
    // その他のドメインの場合
    return hostname;
  };

  // コンポーネントマウント時にcompany_idを取得
  useEffect(() => {
    const id = getCompanyIdFromUrl();
    setCompanyId(id);
  }, []);

  // パスワードリセット処理
  const handlePasswordReset = async () => {
    if (!email) {
      toast({
        title: "入力エラー",
        description: "メールアドレスを入力してください。",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      toast({
        title: "パスワードリセットメール送信",
        description: "パスワードリセット用のメールを送信しました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "エラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // メール確認メール送信
  const handleSendVerificationEmail = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      toast({
        title: "確認メール送信",
        description: "メール確認用のメールを送信しました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "エラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/');
    } catch (error) {
      console.error("Google Login Error:", error);
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "Googleログインエラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };



  const handleEmailSignIn = async (e) => {
    if (!email || !password) {
      toast({
        title: "入力エラー",
        description: "メールアドレスとパスワードを入力してください。",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        toast({
          title: "メール確認が必要です",
          description: "メールアドレスの確認が完了していません。確認メールを再送信しますか？",
          status: "warning",
          duration: 5000,
          isClosable: true,
          action: (
            <Button bg="black" color="white" onClick={handleSendVerificationEmail}>
              確認メールを再送信
            </Button>
          ),
        });
        return;
      }
      navigate('/');
    } catch (error) {
      console.error("Email SignIn Error:", error);
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "ログインエラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={10} display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md">
        <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
          <VStack spacing={6}>
            <Heading size="lg" color="gray.700">{companyId} Selectia</Heading>
            <Button
              w="full"
              size="lg"
              onClick={handleGoogleLogin}
              leftIcon={<img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" style={{ height: '18px' }} />}
              bg="black"
              color="white"
              variant="outline"
              borderColor="gray.300"
            >
              Googleで新規登録/ログイン
            </Button>

            <HStack w="full" alignItems="center">
              <Divider />
              <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">または</Text>
              <Divider />
            </HStack>

            <VStack spacing={4} w="100%">
              <FormControl isRequired>
                <FormLabel>メールアドレス</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  autoComplete="email"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>パスワード</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6文字以上"
                  autoComplete="current-password"
                />
              </FormControl>

              <HStack w="full" spacing={4}>
                <Button
                  type="button"
                  onClick={handleEmailSignIn}
                  bg="black"
                  color="white"
                  flex={1}
                >
                  ログイン
                </Button>
                <Button
                  as={RouterLink}
                  to="/signup"
                  colorScheme="gray"
                  flex={1}
                >
                  新規登録の方はこちら
                </Button>
              </HStack>

              <Button
                variant="link"
                bg="black"
                color="white"
                onClick={handlePasswordReset}
                className="text-sm"
              >
                パスワードをお忘れの方は<br />
                メールアドレスを入力してこちらをクリック
              </Button>

              {currentUser && !currentUser.emailVerified && (
                <Alert status="warning">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>メール確認が必要です</AlertTitle>
                    <AlertDescription>
                      確認メールを送信しました。メールを確認してください。
                      <Button
                        variant="link"
                        bg="black"
                        color="white"
                        onClick={handleSendVerificationEmail}
                        ml={2}
                      >
                        再送信
                      </Button>
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </VStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}
